import { viteEnv } from "utils/vite-env.util";

// Client half of WorkOS SSO, matched to krish/eng-1411-auth-backbone.
// SINGLE configured connection — no provider param. The start is a full-page
// redirect; the backend runs the whole OAuth exchange server-side and redirects
// back with the authtoken in the URL *fragment* — /sso/complete#auth_token=<hex>
// on success, /sso/error#sso_error=<code> on failure.

// Mirrors loginUser's keep-logged-in expiration so RequireAuth rehydrates from
// localStorage after the IdP round-trip wipes Redux (tech-plan D1/D2).
export const SSO_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Full-page redirect into the backend's SSO start. invite_token (acceptance
// flow only) rides through WorkOS state so the backend redeems that invite.
export const ssoStartUrl = (inviteToken?: string): string => {
  const base = `${viteEnv.VITE_SUNO_API}/auth/sso/start/`;
  return inviteToken
    ? `${base}?invite_token=${encodeURIComponent(inviteToken)}`
    : base;
};

// Not a fetch — a top-level navigation (the backend owns the OAuth dance).
export const startSso = (inviteToken?: string): void => {
  window.location.href = ssoStartUrl(inviteToken);
};

// Backend SSO error codes → copy. Mirrors EXACTLY the codes the backend can put
// in the redirect fragment (suno-backend accounts/views/sso.py + services/sso.py;
// the redirect is /sso/error#sso_error=<code>).
export const SSO_ERROR_MESSAGES: Record<string, string> = {
  invite_invalid:
    "This invitation is no longer valid. Ask your administrator to resend it.",
  bad_request: "Something went wrong starting sign-in. Please try again.",
  unknown_practice:
    "We couldn't find the practice for this sign-in. Ask your administrator for a fresh invite.",
  sso_not_configured:
    "Single sign-on isn't set up for this practice yet. Please contact your administrator.",
  bad_state: "Your sign-in session expired. Please start again.",
  provider_error: "Your identity provider reported an error. Please try again.",
  exchange_failed: "We couldn't complete sign-in. Please try again.",
  organization_mismatch:
    "That sign-in doesn't belong to this practice's identity provider. Use the account your invite was sent to.",
  link_conflict: "This sign-in is already linked to a different Suno account.",
  no_account:
    "We couldn't find a Suno account for that sign-in. Ask your administrator to invite you first.",
  practice_mismatch: "That account belongs to a different practice.",
  email_mismatch:
    "The email from your sign-in doesn't match the invitation. Use the account the invite was sent to.",
  account_deactivated:
    "Your account has been deactivated. Please contact your administrator.",
  no_practice:
    "Your account isn't assigned to a practice yet. Please contact your administrator."
};

export const DEFAULT_SSO_ERROR =
  "We couldn't complete sign-in. Please return to login and try again.";

export const ssoErrorMessage = (code?: string | null): string =>
  (code && SSO_ERROR_MESSAGES[code]) || DEFAULT_SSO_ERROR;

// Parse a `#key=value` fragment — the backend returns auth_token / sso_error in
// the hash, never the query string.
export const readHashParam = (key: string): string | null => {
  const hash = window.location.hash.replace(/^#/, "");
  return new URLSearchParams(hash).get(key);
};
